import { computed } from 'vue'
import { useInventoryStore } from '~/stores/useInventoryStore'
import type { OwnedPokemon } from '~/stores/useInventoryStore'
import { getPokemonTypes, TYPES } from '~/data/types'
import { getSlugGeneration } from '~/data/gacha'
import { EVOLUTIONS } from '~/data/evolutions'
import { POKEDEX } from '~/data/pokedex'
import { GENERATION_LABELS } from '~/utils/generationLabels'
import {
  TEAM_SYNERGY_FULL_GEN_GOLD_MULT,
  TEAM_SYNERGY_LEGEND_DUO_DPS_MULT,
  TEAM_SYNERGY_MONOTYPE_DPS_MULT,
  TEAM_SYNERGY_SHINY_MAJORITY_MIN,
  TEAM_SYNERGY_SHINY_MAJORITY_MULT,
  countCompleteEvolutionChains,
  formatEvoChainDpsBonusPercent,
  getEvoChainDpsMult,
} from '../../../shared/team_synergy'

// Legendary duos canoniques (ordre indifférent)
const LEGENDARY_DUOS: Array<[string, string]> = [
  ['lugia', 'hooh'],
  ['dialga', 'palkia'],
  ['reshiram', 'zekrom'],
  ['xerneas', 'yveltal'],
  ['solgaleo', 'lunala'],
  ['zacian', 'zamazenta'],
  ['koraidon', 'miraidon'],
  ['groudon', 'kyogre'],
  ['latios', 'latias'],
  ['cobalion', 'terrakion'],
  ['zygarde', 'xerneas'],
]

export interface SynergyBonus {
  id: string
  labelFr: string
  labelEn: string
  descFr: string
  descEn: string
  icon: string
}

export interface TeamSynergyResult {
  dpsMult: number
  goldMult: number
  shinyMult: number
  activeBonuses: SynergyBonus[]
  nearMissHints: SynergyNearMiss[]
}

export interface SynergyNearMiss {
  id: string
  icon: string
  labelFr: string
  labelEn: string
}

const NEAR_MISS_MAX = 4

const SLUG_DISPLAY_NAMES = new Map<string, { fr: string; en: string }>()
for (const entry of POKEDEX) {
  SLUG_DISPLAY_NAMES.set(entry.slug, { fr: entry.nameFr, en: entry.nameEn })
}
for (const evo of EVOLUTIONS) {
  if (!SLUG_DISPLAY_NAMES.has(evo.toSlug)) {
    SLUG_DISPLAY_NAMES.set(evo.toSlug, { fr: evo.toNameFr, en: evo.toNameEn })
  }
}

const EVO_CHILDREN_BY_FROM = new Map<string, string[]>()
for (const evo of EVOLUTIONS) {
  const list = EVO_CHILDREN_BY_FROM.get(evo.fromSlug)
  if (list) list.push(evo.toSlug)
  else EVO_CHILDREN_BY_FROM.set(evo.fromSlug, [evo.toSlug])
}

function slugDisplayName(slug: string): { fr: string; en: string } {
  return SLUG_DISPLAY_NAMES.get(slug) ?? { fr: slug, en: slug }
}

function computeNearMissHints(
  team: OwnedPokemon[],
  activeIds: Set<string>,
): SynergyNearMiss[] {
  const hints: SynergyNearMiss[] = []
  if (team.length < 2) return hints

  const slugSet = new Set(team.map((p) => p.slug))
  const teamSize = team.length

  if (!activeIds.has('monotype')) {
    const typeCounts = new Map<string, number>()
    for (const poke of team) {
      for (const type of getPokemonTypes(poke)) {
        typeCounts.set(type, (typeCounts.get(type) ?? 0) + 1)
      }
    }
    let bestType: string | null = null
    let bestCount = 0
    for (const [type, count] of typeCounts) {
      if (count > bestCount && count >= teamSize - 1 && count < teamSize) {
        bestCount = count
        bestType = type
      }
    }
    if (bestType) {
      const typeDef = TYPES.find((tp) => tp.id === bestType)
      const typeFr = typeDef?.nameFr ?? bestType
      const typeEn = typeDef?.nameEn ?? bestType
      hints.push({
        id: 'monotype-near',
        icon: '🎯',
        labelFr: `Monotype : ${bestCount}/${teamSize} partagent ${typeFr} — il en manque 1 (+20 % DPS)`,
        labelEn: `Monotype: ${bestCount}/${teamSize} share ${typeEn} — 1 missing (+20% DPS)`,
      })
    }
  }

  if (!activeIds.has('fullgen')) {
    const genCounts = new Map<number, number>()
    for (const poke of team) {
      const gen = getSlugGeneration(poke.slug)
      if (gen) genCounts.set(gen, (genCounts.get(gen) ?? 0) + 1)
    }
    let bestGen = 0
    let bestCount = 0
    for (const [gen, count] of genCounts) {
      if (count > bestCount && count >= teamSize - 1 && count < teamSize) {
        bestCount = count
        bestGen = gen
      }
    }
    if (bestGen > 0) {
      const label = GENERATION_LABELS[bestGen]
      const regionFr = label?.fr ?? `Gen ${bestGen}`
      const regionEn = label?.en ?? `Gen ${bestGen}`
      hints.push({
        id: 'fullgen-near',
        icon: '🏅',
        labelFr: `Régionale : ${bestCount}/${teamSize} de ${regionFr} — il en manque 1 (+10 % or)`,
        labelEn: `Regional: ${bestCount}/${teamSize} from ${regionEn} — 1 missing (+10% gold)`,
      })
    }
  }

  if (hints.length < NEAR_MISS_MAX) {
    const usedChains = new Set<string>()
    for (const evo1 of EVOLUTIONS) {
      if (hints.length >= NEAR_MISS_MAX) break
      const thirdStages = EVO_CHILDREN_BY_FROM.get(evo1.toSlug) ?? []
      for (const third of thirdStages) {
        const slugs = [evo1.fromSlug, evo1.toSlug, third]
        const present = slugs.filter((s) => slugSet.has(s))
        if (present.length !== 2) continue
        const chainKey = slugs.join('|')
        if (usedChains.has(chainKey)) continue
        usedChains.add(chainKey)
        const missing = slugs.find((s) => !slugSet.has(s))!
        const name = slugDisplayName(missing)
        hints.push({
          id: `evochain-near-${chainKey}`,
          icon: '🔗',
          labelFr: `Chaîne évolutive incomplète — il manque ${name.fr} (+8 % DPS)`,
          labelEn: `Incomplete evo chain — missing ${name.en} (+8% DPS)`,
        })
        if (hints.length >= NEAR_MISS_MAX) break
      }
    }
  }

  if (!activeIds.has('legendduo') && hints.length < NEAR_MISS_MAX) {
    for (const [a, b] of LEGENDARY_DUOS) {
      const hasA = slugSet.has(a)
      const hasB = slugSet.has(b)
      if (!hasA && !hasB) continue
      if (hasA && hasB) continue
      const missing = hasA ? b : a
      const name = slugDisplayName(missing)
      hints.push({
        id: `legendduo-near-${missing}`,
        icon: '⚡',
        labelFr: `Duo légendaire : il manque ${name.fr} (+25 % DPS)`,
        labelEn: `Legendary duo: missing ${name.en} (+25% DPS)`,
      })
      break
    }
  }

  if (!activeIds.has('shinymajority') && hints.length < NEAR_MISS_MAX) {
    const shinyCount = team.filter((p) => p.isShiny).length
    if (shinyCount > 0 && shinyCount < TEAM_SYNERGY_SHINY_MAJORITY_MIN) {
      const missing = TEAM_SYNERGY_SHINY_MAJORITY_MIN - shinyCount
      hints.push({
        id: 'shiny-near',
        icon: '✨',
        labelFr: `Majorité chromatique : ${shinyCount}/6 ✨ — encore ${missing} pour +10 % taux shiny`,
        labelEn: `Shiny majority: ${shinyCount}/6 ✨ — ${missing} more for +10% shiny rate`,
      })
    }
  }

  return hints.slice(0, NEAR_MISS_MAX)
}

export interface ComputeTeamSynergyOptions {
  /** Near-miss hints are UI-only and expensive — skip in hot paths (optimizer, combat). */
  includeNearMissHints?: boolean
}

function computeTeamSynergyCore(
  team: OwnedPokemon[],
  options: ComputeTeamSynergyOptions = {},
): TeamSynergyResult {
  const includeNearMissHints = options.includeNearMissHints !== false

  if (team.length === 0) {
    return { dpsMult: 1, goldMult: 1, shinyMult: 1, activeBonuses: [], nearMissHints: [] }
  }

  let dpsMult = 1
  let goldMult = 1
  let shinyMult = 1
  const activeBonuses: SynergyBonus[] = []

  // 1. Équipe monotype → +20% DPS (au moins un type partagé par tous les membres)
  const firstMember = team[0]
  if (team.length >= 2 && firstMember) {
    let sharedTypes = new Set(getPokemonTypes(firstMember))
    for (let i = 1; i < team.length; i++) {
      const member = team[i]
      if (!member) continue
      const memberTypes = new Set(getPokemonTypes(member))
      sharedTypes = new Set([...sharedTypes].filter((t) => memberTypes.has(t)))
    }
    if (sharedTypes.size > 0) {
      dpsMult *= TEAM_SYNERGY_MONOTYPE_DPS_MULT
      activeBonuses.push({
        id: 'monotype',
        labelFr: 'Équipe Monotype',
        labelEn: 'Monotype Team',
        descFr: '+20% DPS (type en commun à toute l\'équipe)',
        descEn: '+20% DPS (type shared by entire team)',
        icon: '🎯',
      })
    }
  }

  // 2. Équipe de même génération → +10% or
  if (team.length >= 2) {
    const teamGens = team.map((p) => getSlugGeneration(p.slug))
    const firstGen = teamGens[0]
    if (firstGen && teamGens.every((g) => g === firstGen)) {
      goldMult *= TEAM_SYNERGY_FULL_GEN_GOLD_MULT
      activeBonuses.push({
        id: 'fullgen',
        labelFr: 'Équipe Régionale',
        labelEn: 'Full Gen Team',
        descFr: '+10% Or (même génération)',
        descEn: '+10% Gold (same generation)',
        icon: '🏅',
      })
    }
  }

  // 3. Chaînes évolutives complètes → +8% DPS par chaîne (max 2)
  const chainsCount = countCompleteEvolutionChains(
    team.map((p) => p.slug),
    EVOLUTIONS,
  )

  if (chainsCount > 0) {
    const chainMult = getEvoChainDpsMult(chainsCount)
    dpsMult *= chainMult
    const bonusPct = formatEvoChainDpsBonusPercent(chainsCount)
    activeBonuses.push({
      id: 'evochain',
      labelFr: `Chaîne Évolutive ×${chainsCount}`,
      labelEn: `Evo Chain ×${chainsCount}`,
      descFr: `+${bonusPct}% DPS (max 2 chaînes comptées)`,
      descEn: `+${bonusPct}% DPS (max 2 chains counted)`,
      icon: '🔗',
    })
  }

  // 4. Duo légendaire canon → +25% DPS
  const slugList = team.map((p) => p.slug)
  const hasDuo = LEGENDARY_DUOS.some(([a, b]) => slugList.includes(a) && slugList.includes(b))
  if (hasDuo) {
    dpsMult *= TEAM_SYNERGY_LEGEND_DUO_DPS_MULT
    activeBonuses.push({
      id: 'legendduo',
      labelFr: 'Duo Légendaire',
      labelEn: 'Legendary Duo',
      descFr: '+25% DPS (paire canonique)',
      descEn: '+25% DPS (canonical pair)',
      icon: '⚡',
    })
  }

  // 5. Majorité chromatique (4+ shinies) → +10% taux shiny
  const shinyCount = team.filter((p) => p.isShiny).length
  if (shinyCount >= TEAM_SYNERGY_SHINY_MAJORITY_MIN) {
    shinyMult *= TEAM_SYNERGY_SHINY_MAJORITY_MULT
    activeBonuses.push({
      id: 'shinymajority',
      labelFr: 'Majorité Chromatique',
      labelEn: 'Shiny Majority',
      descFr: `+10% Taux Shiny (${shinyCount}/6 ✨)`,
      descEn: `+10% Shiny Rate (${shinyCount}/6 ✨)`,
      icon: '✨',
    })
  }

  const nearMissHints = includeNearMissHints
    ? computeNearMissHints(team, new Set(activeBonuses.map((b) => b.id)))
    : []

  return { dpsMult, goldMult, shinyMult, activeBonuses, nearMissHints }
}

export function computeTeamSynergy(
  team: OwnedPokemon[],
  options?: ComputeTeamSynergyOptions,
): TeamSynergyResult {
  return computeTeamSynergyCore(team, options)
}

/** Multiplicateur DPS synergie seul — sans hints UI (optimiseur d'équipe). */
export function computeTeamSynergyDpsMult(team: OwnedPokemon[]): number {
  return computeTeamSynergyCore(team, { includeNearMissHints: false }).dpsMult
}

export function useTeamSynergy() {
  const inventory = useInventoryStore()

  const synergy = computed((): TeamSynergyResult => computeTeamSynergy(inventory.team))

  return { synergy }
}
